import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HandHelping, MapPin, Move, Package, QrCode, RotateCcw, Wrench } from "lucide-react";

import {
  useAsset,
  useEvents,
  useLoans,
  useMoveAsset,
  useSetAssetCondition,
} from "@/api/queries";
import type { AssetCondition, AssetLifecycle, Loan } from "@/api/types";
import { useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { LabelDialog } from "@/features/labels/LabelDialog";
import { CheckOutDialog } from "@/features/loans/CheckOutDialog";
import { ReturnDialog } from "@/features/loans/ReturnDialog";
import { PlacePicker } from "@/features/ingestion/PlacePicker";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import {
  formatAcquisitionDate,
  formatAssetCondition,
  formatAssetLifecycle,
  formatDate,
  formatDateTime,
  formatOverdue,
  formatOwnership,
  formatQuantity,
  formatRelative,
} from "@/lib/format";
import { assetTone } from "@/lib/stock";

const CONDITIONS: AssetCondition[] = [
  "NEW",
  "GOOD",
  "WORN",
  "NEEDS_REPAIR",
  "DAMAGED",
  "UNKNOWN",
];

const LIFECYCLES: AssetLifecycle[] = [
  "IN_SERVICE",
  "IN_REPAIR",
  "OUT_OF_SERVICE",
  "LOST",
  "DISCARDED",
  "SOLD",
];

/** The asset sheet: what this particular tool is, where it is, and its history. */
export function AssetDetailPage() {
  const { assetId = "" } = useParams();
  const canWrite = useCanWrite();
  const [moving, setMoving] = useState(false);
  const [grading, setGrading] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [lending, setLending] = useState(false);
  const [returning, setReturning] = useState<Loan | null>(null);

  const { data: asset, isLoading, error, refetch } = useAsset(assetId);
  const history = useEvents({ entity_type: "asset", entity_id: assetId, limit: 50 });
  // The open loan, asked for by asset. The server decides what "active" means;
  // filtering a full loan list here would be the client inventing the rule.
  const loans = useLoans({ asset_id: assetId, status: "ACTIVE", limit: 1 });
  const activeLoan = loans.data?.items[0] ?? null;

  if (isLoading) return <LoadingState label="Chargement de l'outil…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!asset) return null;

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: "Outils / Assets", to: "/assets" }, { label: asset.asset_code }]}
        title={asset.name ?? asset.product_name}
        subtitle={asset.asset_code}
        actions={
          <>
            <Button variant="secondary" onClick={() => setLabelOpen(true)}>
              <QrCode className="size-4" aria-hidden />
              Étiquette
            </Button>
            {canWrite && (
              <>
                <Button variant="secondary" onClick={() => setGrading(true)}>
                  <Wrench className="size-4" aria-hidden />
                  Changer l'état
                </Button>
                <Button variant="secondary" onClick={() => setMoving(true)}>
                  <Move className="size-4" aria-hidden />
                  Déplacer
                </Button>
                {activeLoan ? (
                  <Button variant="primary" onClick={() => setReturning(activeLoan)}>
                    <RotateCcw className="size-4" aria-hidden />
                    Retour
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => setLending(true)}
                    disabled={!asset.is_available}
                    title={
                      asset.is_available
                        ? undefined
                        : "Cet outil n'est pas en service; il ne peut pas être prêté."
                    }
                  >
                    <HandHelping className="size-4" aria-hidden />
                    Prêter
                  </Button>
                )}
              </>
            )}
          </>
        }
      />

      <div className="grid min-w-0 gap-3 @4xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardBody className="flex flex-col gap-3">
              <Photo
                mediaId={asset.media_id}
                alt={asset.product_name}
                className="aspect-square w-full"
                rounded="rounded-lg"
              />

              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={assetTone(asset.lifecycle)}>
                  {formatAssetLifecycle(asset.lifecycle)}
                </StatusBadge>
                {activeLoan ? (
                  <StatusBadge tone={activeLoan.is_overdue ? "out" : "loaned"}>
                    {activeLoan.is_overdue ? "En retard" : "Prêté"}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone={asset.is_available ? "available" : "out"}>
                    {asset.is_available ? "Disponible" : "Indisponible"}
                  </StatusBadge>
                )}
              </div>

              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
                <Definition label="Code" value={asset.asset_code} mono />
                <Definition label="N° de série" value={asset.serial_number} mono />
                <Definition label="État" value={formatAssetCondition(asset.condition)} />
                <Definition label="Propriété" value={formatOwnership(asset.ownership)} />
                <Definition
                  label="Acquisition"
                  value={formatAcquisitionDate(asset.acquisition_date, asset.acquisition_precision)}
                />
                <Definition
                  label="Coût"
                  value={asset.acquisition_cost ? `${formatQuantity(asset.acquisition_cost)} $` : null}
                />
                <Definition label="Garantie" value={formatDate(asset.warranty_expires_on)} />
                <Definition label="Calibration" value={formatDate(asset.calibration_due_on)} />
              </dl>

              {asset.provenance && (
                <p className="border-t border-border pt-3 text-[13px] text-ink-muted">
                  Provenance : {asset.provenance}
                </p>
              )}
              {asset.notes && (
                <p className="border-t border-border pt-3 text-[13px] text-ink-muted">
                  {asset.notes}
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Prêt actif" />
            <CardBody>
              {activeLoan ? (
                <div className="flex flex-col gap-2 text-[13px]">
                  <p className="text-ink">
                    <span className="text-ink-subtle">Chez </span>
                    {activeLoan.person_name}
                  </p>
                  <p className="text-ink-muted">
                    Sorti le {formatDate(activeLoan.checked_out_at)} ·{" "}
                    {activeLoan.due_on ? (
                      <span className={activeLoan.is_overdue ? "text-status-out" : undefined}>
                        retour prévu le {formatDate(activeLoan.due_on)}
                        {formatOverdue(activeLoan.days_overdue)
                          ? ` (${formatOverdue(activeLoan.days_overdue)})`
                          : ""}
                      </span>
                    ) : (
                      "sans échéance"
                    )}
                  </p>
                  {activeLoan.notes_out && (
                    <p className="text-ink-muted">{activeLoan.notes_out}</p>
                  )}
                  <Link to="/loans" className="text-[12px] text-brand hover:underline">
                    Voir tous les prêts
                  </Link>
                </div>
              ) : (
                <p className="text-[13px] text-ink-subtle">
                  Cet outil n'est prêté à personne.
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Emplacement actuel" />
            <CardBody>
              {asset.place_label ? (
                <p className="flex items-center gap-2 text-[13px] text-ink">
                  <MapPin className="size-4 shrink-0 text-ink-subtle" aria-hidden />
                  {asset.current_container_id ? (
                    <Link
                      to={`/containers/${asset.current_container_id}`}
                      className="hover:text-brand"
                    >
                      {asset.place_label}
                    </Link>
                  ) : asset.current_location_id ? (
                    <Link
                      to={`/locations/${asset.current_location_id}`}
                      className="hover:text-brand"
                    >
                      {asset.place_label}
                    </Link>
                  ) : (
                    asset.place_label
                  )}
                </p>
              ) : (
                <p className="text-[13px] text-ink-subtle">Aucun emplacement enregistré.</p>
              )}
              {asset.current_container_id && (
                <p className="mt-2 text-[12px] text-ink-subtle">
                  Rangé dans un contenant : déplacer le contenant déplace l'outil avec lui.
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Produit" />
            <CardBody>
              <Link
                to={`/products/${asset.product_id}`}
                className="flex items-center gap-2 text-[13px] text-ink hover:text-brand"
              >
                <Package className="size-4 shrink-0 text-ink-subtle" aria-hidden />
                {asset.product_name}
              </Link>
              <p className="mt-1 text-[12px] text-ink-subtle">
                {[asset.product_manufacturer, asset.product_part_number].filter(Boolean).join(" · ")}
              </p>
            </CardBody>
          </Card>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden">
          <CardHeader
            className="shrink-0"
            title="Historique"
            subtitle="Créations, déplacements et changements d'état."
          />
          <div className="min-w-0">
            {history.isLoading && <LoadingState />}
            {history.data && history.data.items.length === 0 && (
              <EmptyState title="Aucun événement" description="L'historique apparaîtra ici." />
            )}
            {history.data && history.data.items.length > 0 && (
              <ul className="divide-y divide-border/60">
                {history.data.items.map((event) => (
                  <li key={event.id} className="px-4 py-2.5">
                    <p className="text-[13px] text-ink">{describe(event.event_type)}</p>
                    <p className="text-[11px] text-ink-subtle">
                      {formatDateTime(event.occurred_at)} · {formatRelative(event.occurred_at)}
                    </p>
                    {typeof event.payload_json.notes === "string" && (
                      <p className="mt-1 text-[12px] text-ink-muted">
                        {String(event.payload_json.notes)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <CheckOutDialog open={lending} onOpenChange={setLending} assetId={assetId} />
      <ReturnDialog
        loan={returning}
        onOpenChange={(open) => {
          if (!open) setReturning(null);
        }}
      />

      <LabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        entity="asset"
        entityId={assetId}
      />
      <MoveAssetDialog open={moving} onOpenChange={setMoving} assetId={assetId} />
      <GradeAssetDialog
        open={grading}
        onOpenChange={setGrading}
        assetId={assetId}
        condition={asset.condition}
        lifecycle={asset.lifecycle}
      />
    </Page>
  );
}

function describe(eventType: string): string {
  switch (eventType) {
    case "ASSET_CREATED":
      return "Outil enregistré";
    case "ASSET_MOVED":
      return "Outil déplacé";
    case "ASSET_CONDITION_CHANGED":
      return "État modifié";
    case "IDENTIFIER_ADDED":
      return "Identifiant ajouté";
    case "MEDIA_ADDED":
      return "Photo ajoutée";
    default:
      return eventType.replace(/_/g, " ").toLowerCase();
  }
}

function Definition({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className={mono ? "truncate font-mono text-ink" : "truncate text-ink"}>
        {value || "—"}
      </dd>
    </div>
  );
}

function MoveAssetDialog({
  open,
  onOpenChange,
  assetId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
}) {
  const toast = useToast();
  const [place, setPlace] = useState<{
    location_id: string | null;
    container_id: string | null;
  }>({ location_id: null, container_id: null });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMoveAsset();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError(null);
        onOpenChange(next);
      }}
      title="Déplacer l'outil"
      description="Rangez-le dans un contenant et il suivra ce contenant lors de ses déplacements."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!place.location_id && !place.container_id}
            onClick={() => {
              setError(null);
              mutation.mutate(
                { assetId, ...place },
                {
                  onSuccess: () => {
                    toast.success("Outil déplacé");
                    onOpenChange(false);
                  },
                  onError: (cause) => setError(cause.message),
                },
              );
            }}
          >
            Déplacer
          </Button>
        </>
      }
    >
      <PlacePicker
        locationId={place.location_id}
        containerId={place.container_id}
        onChange={setPlace}
        label="Nouvelle destination"
      />
      {error && (
        <p role="alert" className="mt-3 rounded-md bg-danger-subtle px-3 py-2 text-[13px] text-danger">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function GradeAssetDialog({
  open,
  onOpenChange,
  assetId,
  condition,
  lifecycle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  condition: AssetCondition;
  lifecycle: AssetLifecycle;
}) {
  const toast = useToast();
  const [nextCondition, setNextCondition] = useState<AssetCondition>(condition);
  const [nextLifecycle, setNextLifecycle] = useState<AssetLifecycle>(lifecycle);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useSetAssetCondition();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError(null);
        onOpenChange(next);
      }}
      title="Changer l'état"
      description="L'usure et la disponibilité sont deux choses différentes : un outil usé fonctionne encore."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate(
                {
                  assetId,
                  condition: nextCondition,
                  lifecycle: nextLifecycle,
                  notes: notes.trim() || undefined,
                },
                {
                  onSuccess: () => {
                    toast.success("État mis à jour");
                    onOpenChange(false);
                  },
                  onError: (cause) => setError(cause.message),
                },
              );
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="État physique" htmlFor="asset-condition" hint="L'usure constatée.">
          <Select
            id="asset-condition"
            value={nextCondition}
            onChange={(event) => setNextCondition(event.target.value as AssetCondition)}
          >
            {CONDITIONS.map((value) => (
              <option key={value} value={value}>
                {formatAssetCondition(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Statut"
          htmlFor="asset-lifecycle"
          hint="Seul « En service » permet de promettre l'outil à quelqu'un."
        >
          <Select
            id="asset-lifecycle"
            value={nextLifecycle}
            onChange={(event) => setNextLifecycle(event.target.value as AssetLifecycle)}
          >
            {LIFECYCLES.map((value) => (
              <option key={value} value={value}>
                {formatAssetLifecycle(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Note" htmlFor="asset-notes" hint="Ce qui est constaté, facultatif.">
          <Input
            id="asset-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Ex. : mandrin qui glisse"
          />
        </Field>
        {error && (
          <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-[13px] text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
